import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ArrowDown, ArrowUp, ArrowUpDown, PackageCheck, Plus, Search, Trash2 } from 'lucide-react';
import { useUIColors } from '../../hooks/useTheme';

const formatDate = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('es-CL');
};

const Stacked = ({ top, bottom, topColor, mutedColor }) => (
  <div style={{ lineHeight: 1.25 }}>
    <div style={{ fontWeight: 700, fontSize: '12px', color: topColor || '#0f172a' }}>{top || '—'}</div>
    {bottom ? (
      <div style={{ fontSize: '11px', color: mutedColor || '#64748b', marginTop: '1px' }}>{bottom}</div>
    ) : null}
  </div>
);

const statusPresentation = (statusRaw, isDarkMode) => {
  const s = String(statusRaw || '').trim().toLowerCase();
  const base = { label: statusRaw && String(statusRaw).trim() ? String(statusRaw).trim() : '—' };
  if (s === 'confirmada') {
    return { ...base, bg: isDarkMode ? '#14532d' : '#dcfce7', color: isDarkMode ? '#bbf7d0' : '#166534', border: isDarkMode ? '#22c55e' : '#86efac' };
  }
  if (s === 'anulada') {
    return { ...base, bg: isDarkMode ? '#7f1d1d' : '#fee2e2', color: isDarkMode ? '#fca5a5' : '#b91c1c', border: isDarkMode ? '#ef4444' : '#fecaca' };
  }
  return { ...base, bg: isDarkMode ? '#27272a' : '#f0ebe3', color: isDarkMode ? '#e4e4e7' : '#44403c', border: isDarkMode ? '#3f3f46' : '#d6d0c4' };
};

const TABLE_COLUMNS = [
  { key: 'Código', align: 'left', sortKey: 'receipt_code' },
  { key: 'OC', align: 'left', sortKey: 'purchase_order_code' },
  { key: 'Proveedor', align: 'left', sortKey: 'supplier_name' },
  { key: 'Fecha', align: 'left', sortKey: 'receipt_date' },
  { key: 'Recibido por', align: 'left', sortKey: 'received_by' },
  { key: 'Ítems', align: 'right', sortKey: 'items_count' },
  { key: 'Estado', align: 'center', sortKey: 'status' },
];

const getSortValue = (row, sortKey) => {
  switch (sortKey) {
    case 'receipt_code':
      return String(row.receipt_code || '').toLowerCase();
    case 'purchase_order_code':
      return String(row.purchase_order_code || '').toLowerCase();
    case 'supplier_name':
      return String(row.supplier_name || '').toLowerCase();
    case 'receipt_date':
      return row.receipt_date ? new Date(row.receipt_date).getTime() : 0;
    case 'received_by':
      return String(row.received_by || '').toLowerCase();
    case 'items_count':
      return Number(row.items_count || 0);
    case 'status':
      return String(row.status || '').toLowerCase();
    default:
      return '';
  }
};

const compareRows = (a, b, sortKey, sortDir) => {
  const mult = sortDir === 'asc' ? 1 : -1;
  const va = getSortValue(a, sortKey);
  const vb = getSortValue(b, sortKey);
  if (typeof va === 'number' && typeof vb === 'number') return (va - vb) * mult;
  return String(va).localeCompare(String(vb), 'es', { numeric: true, sensitivity: 'base' }) * mult;
};

const PurchaseReceiptsTable = ({
  rows = [],
  loading = false,
  error = '',
  isDarkMode = false,
  tableTitle = 'Recepciones',
  addButtonLabel = 'Nueva recepción',
  hideChrome = false,
  onAdd,
  onRowClick,
  onBulkDelete,
  onConfirm,
}) => {
  const ui = useUIColors();
  const bg = ui.page;
  const headerBg = ui.header;
  const border = ui.border;
  const text = ui.text;
  const muted = ui.muted;
  const rowHover = ui.rowHover;
  const padV = 9;
  const padH = 10;
  const thPadV = 8;
  const inputBg = ui.inputBg;
  const inputBorder = ui.inputBorder;

  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [sortColumn, setSortColumn] = useState('receipt_date');
  const [sortDir, setSortDir] = useState('desc');
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkPending, setBulkPending] = useState(false);
  const [bulkError, setBulkError] = useState('');
  const headerSelectRef = useRef(null);

  const showBulkChrome = Boolean(onBulkDelete);
  const showActions = Boolean(onConfirm);
  const colCount = TABLE_COLUMNS.length + (showBulkChrome ? 1 : 0) + (showActions ? 1 : 0);

  const statusOptions = useMemo(() => {
    const map = new Map();
    rows.forEach((r) => {
      const st = String(r.status || '').trim();
      if (st) map.set(st, st);
    });
    return Array.from(map.values()).sort((a, b) => a.localeCompare(b, 'es'));
  }, [rows]);

  const filteredRows = useMemo(() => {
    let list = rows;
    const tokens = searchQuery.trim().toLowerCase().split(/\s+/).filter(Boolean);
    if (tokens.length) {
      list = list.filter((row) => {
        const hay = [
          row.receipt_code,
          row.purchase_order_code,
          row.supplier_name,
          row.received_by,
          row.status,
          row.notes,
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        return tokens.every((t) => hay.includes(t));
      });
    }
    if (filterStatus) list = list.filter((row) => String(row.status || '').trim() === filterStatus);
    return list;
  }, [rows, searchQuery, filterStatus]);

  const sortedRows = useMemo(() => {
    const arr = [...filteredRows];
    arr.sort((a, b) => compareRows(a, b, sortColumn, sortDir));
    return arr;
  }, [filteredRows, sortColumn, sortDir]);

  const visibleIds = useMemo(
    () => sortedRows.map((r) => Number(r.id)).filter((id) => Number.isFinite(id)),
    [sortedRows]
  );
  const allVisibleSelected =
    showBulkChrome && visibleIds.length > 0 && visibleIds.every((id) => selectedIds.includes(id));
  const someVisibleSelected = showBulkChrome && visibleIds.some((id) => selectedIds.includes(id));

  useEffect(() => {
    const el = headerSelectRef.current;
    if (!el || el.type !== 'checkbox') return;
    el.indeterminate = someVisibleSelected && !allVisibleSelected;
  }, [someVisibleSelected, allVisibleSelected, showBulkChrome]);

  const toggleSelectOne = (rawId) => {
    const id = Number(rawId);
    if (!Number.isFinite(id)) return;
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const toggleSelectAllVisible = () => {
    setSelectedIds((prev) => {
      if (visibleIds.length && visibleIds.every((id) => prev.includes(id))) {
        return prev.filter((id) => !visibleIds.includes(id));
      }
      const next = new Set(prev);
      visibleIds.forEach((id) => next.add(id));
      return Array.from(next);
    });
  };

  const handleSortClick = (key) => {
    if (sortColumn === key) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else {
      setSortColumn(key);
      setSortDir(key.includes('date') || key === 'items_count' ? 'desc' : 'asc');
    }
  };

  const hasActiveFilters = Boolean(searchQuery.trim() || filterStatus);
  const clearFilters = () => {
    setSearchQuery('');
    setFilterStatus('');
  };

  const handleBulkDelete = async () => {
    if (!onBulkDelete || !selectedIds.length) return;
    setBulkPending(true);
    setBulkError('');
    try {
      const result = await onBulkDelete(selectedIds);
      if (result !== false) setSelectedIds([]);
    } catch (err) {
      setBulkError(err?.message || 'No se pudieron eliminar.');
    } finally {
      setBulkPending(false);
    }
  };

  const subtitle = loading
    ? 'Cargando…'
    : error ||
      (hasActiveFilters
        ? `Mostrando ${sortedRows.length} de ${rows.length} registros`
        : `${rows.length} registros`);

  const selectStyle = {
    height: '36px',
    padding: '0 10px',
    borderRadius: '8px',
    border: `1px solid ${inputBorder}`,
    background: inputBg,
    color: text,
    fontSize: '13px',
    minWidth: '140px',
    cursor: 'pointer',
    outline: 'none',
  };

  const SortIcon = ({ columnKey }) => {
    if (sortColumn !== columnKey) return <ArrowUpDown size={12} color={muted} />;
    return sortDir === 'asc' ? <ArrowUp size={12} color={text} /> : <ArrowDown size={12} color={text} />;
  };

  const iconButtonStyle = (enabled) => ({
    width: 28,
    height: 28,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 6,
    border: `1px solid ${inputBorder}`,
    background: enabled ? inputBg : isDarkMode ? '#18181b' : '#f8fafc',
    color: enabled ? text : muted,
    cursor: enabled ? 'pointer' : 'not-allowed',
    opacity: enabled ? 1 : 0.45,
  });

  const canConfirmRow = (row) => {
    const s = String(row?.status || '').trim().toLowerCase();
    return s === 'borrador' || s === '';
  };

  return (
    <div
      style={
        hideChrome
          ? { background: bg }
          : {
              border: `1px solid ${border}`,
              borderRadius: '12px',
              overflow: 'hidden',
              background: bg,
            }
      }
    >
      {hideChrome ? null : (
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '12px 16px',
          borderBottom: `1px solid ${border}`,
          background: headerBg,
          flexWrap: 'wrap',
          gap: '10px',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'baseline', gap: '12px', flexWrap: 'wrap', minWidth: 0 }}>
          <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 700, color: text }}>{tableTitle}</h3>
          <p style={{ margin: 0, fontSize: '12px', color: muted }}>{subtitle}</p>
        </div>
        {onAdd ? (
          <button
            type="button"
            onClick={onAdd}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              height: '36px',
              padding: '0 14px',
              borderRadius: '8px',
              border: 'none',
              background: '#16a34a',
              color: '#fff',
              fontWeight: 600,
              fontSize: '13px',
              cursor: 'pointer',
            }}
          >
            <Plus size={16} /> {addButtonLabel}
          </button>
        ) : null}
      </div>
      )}

      <div
        style={{
          padding: '10px 16px',
          borderBottom: `1px solid ${border}`,
          background: bg,
          display: 'flex',
          flexWrap: 'wrap',
          gap: '10px',
          alignItems: 'flex-end',
        }}
      >
        <label style={{ display: 'flex', flexDirection: 'column', gap: '6px', flex: '1 1 220px' }}>
          <span style={{ fontSize: '11px', fontWeight: 600, color: muted, letterSpacing: '0.04em' }}>Buscar</span>
          <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Search size={16} color={muted} aria-hidden />
            <input
              type="search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Código, OC, proveedor…"
              style={{ ...selectStyle, flex: 1, minWidth: 0, cursor: 'text' }}
            />
          </span>
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: '6px', minWidth: '140px' }}>
          <span style={{ fontSize: '11px', fontWeight: 600, color: muted, letterSpacing: '0.04em' }}>Estado</span>
          <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} style={selectStyle}>
            <option value="">Todos</option>
            {statusOptions.map((st) => (
              <option key={st} value={st}>
                {st}
              </option>
            ))}
          </select>
        </label>
        {hasActiveFilters ? (
          <button
            type="button"
            onClick={clearFilters}
            style={{
              height: '36px',
              padding: '0 12px',
              borderRadius: '8px',
              border: `1px solid ${inputBorder}`,
              background: inputBg,
              color: text,
              fontSize: '13px',
              cursor: 'pointer',
            }}
          >
            Limpiar
          </button>
        ) : null}
      </div>

      {showBulkChrome && selectedIds.length > 0 ? (
        <div
          style={{
            padding: '10px 16px',
            borderBottom: `1px solid ${border}`,
            background: isDarkMode ? '#1c1917' : '#fafaf9',
            display: 'flex',
            flexWrap: 'wrap',
            gap: '10px',
            alignItems: 'center',
          }}
        >
          <span style={{ fontSize: '13px', color: text, fontWeight: 600 }}>
            {selectedIds.length} seleccionado{selectedIds.length === 1 ? '' : 's'}
          </span>
          <button
            type="button"
            disabled={bulkPending}
            onClick={handleBulkDelete}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              height: '32px',
              padding: '0 12px',
              borderRadius: '6px',
              border: 'none',
              background: '#dc2626',
              color: '#fff',
              fontWeight: 600,
              fontSize: '12px',
              cursor: bulkPending ? 'wait' : 'pointer',
              opacity: bulkPending ? 0.7 : 1,
            }}
          >
            <Trash2 size={14} /> Eliminar
          </button>
          {bulkError ? <span style={{ fontSize: '12px', color: '#dc2626' }}>{bulkError}</span> : null}
        </div>
      ) : null}

      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '920px' }}>
          <thead>
            <tr style={{ background: headerBg, borderBottom: `1px solid ${border}` }}>
              {showBulkChrome ? (
                <th style={{ padding: `${thPadV}px ${padH}px`, width: 36, textAlign: 'center' }}>
                  <input
                    ref={headerSelectRef}
                    type="checkbox"
                    checked={allVisibleSelected}
                    onChange={toggleSelectAllVisible}
                    aria-label="Seleccionar todos"
                  />
                </th>
              ) : null}
              {TABLE_COLUMNS.map((col) => (
                <th
                  key={col.key}
                  style={{
                    padding: `${thPadV}px ${padH}px`,
                    textAlign: col.align,
                    fontSize: '11px',
                    fontWeight: 700,
                    color: muted,
                    letterSpacing: '0.04em',
                    textTransform: 'uppercase',
                    whiteSpace: 'nowrap',
                    cursor: 'pointer',
                    userSelect: 'none',
                  }}
                  onClick={() => handleSortClick(col.sortKey)}
                >
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                    {col.key}
                    <SortIcon columnKey={col.sortKey} />
                  </span>
                </th>
              ))}
              {showActions ? (
                <th
                  style={{
                    padding: `${thPadV}px ${padH}px`,
                    textAlign: 'right',
                    fontSize: '11px',
                    fontWeight: 700,
                    color: muted,
                    letterSpacing: '0.04em',
                    textTransform: 'uppercase',
                    whiteSpace: 'nowrap',
                  }}
                >
                  Acciones
                </th>
              ) : null}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={colCount} style={{ padding: 24, textAlign: 'center', color: muted }}>
                  Cargando recepciones…
                </td>
              </tr>
            ) : error ? (
              <tr>
                <td colSpan={colCount} style={{ padding: 24, textAlign: 'center', color: '#dc2626' }}>
                  {error}
                </td>
              </tr>
            ) : sortedRows.length === 0 ? (
              <tr>
                <td colSpan={colCount} style={{ padding: 24, textAlign: 'center', color: muted }}>
                  No hay recepciones para mostrar.
                </td>
              </tr>
            ) : (
              sortedRows.map((row) => {
                const st = statusPresentation(row.status, isDarkMode);
                const selected = selectedIds.includes(Number(row.id));
                return (
                  <tr
                    key={row.id}
                    style={{ borderBottom: `1px solid ${border}`, background: selected ? rowHover : bg }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = rowHover;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = selected ? rowHover : bg;
                    }}
                  >
                    {showBulkChrome ? (
                      <td style={{ padding: `${padV}px ${padH}px`, textAlign: 'center' }}>
                        <input
                          type="checkbox"
                          checked={selected}
                          onChange={() => toggleSelectOne(row.id)}
                          aria-label={`Seleccionar ${row.receipt_code || row.id}`}
                        />
                      </td>
                    ) : null}
                    <td style={{ padding: `${padV}px ${padH}px` }}>
                      <button
                        type="button"
                        onClick={() => onRowClick?.(row)}
                        style={{
                          border: 'none',
                          background: 'transparent',
                          padding: 0,
                          cursor: 'pointer',
                          color: text,
                          fontWeight: 700,
                          fontSize: 12,
                        }}
                      >
                        {row.receipt_code || '—'}
                      </button>
                    </td>
                    <td style={{ padding: `${padV}px ${padH}px`, fontSize: 12, color: text, fontWeight: 600 }}>
                      {row.purchase_order_code || '—'}
                    </td>
                    <td style={{ padding: `${padV}px ${padH}px` }}>
                      <Stacked top={row.supplier_name} bottom={row.supplier_code || ''} topColor={text} mutedColor={muted} />
                    </td>
                    <td style={{ padding: `${padV}px ${padH}px`, fontSize: 12, color: muted }}>
                      {formatDate(row.receipt_date)}
                    </td>
                    <td style={{ padding: `${padV}px ${padH}px`, fontSize: 12, color: text }}>
                      {row.received_by || '—'}
                    </td>
                    <td style={{ padding: `${padV}px ${padH}px`, fontSize: 12, color: text, textAlign: 'right', fontWeight: 600 }}>
                      {row.items_count ?? 0}
                    </td>
                    <td style={{ padding: `${padV}px ${padH}px`, textAlign: 'center' }}>
                      <span
                        style={{
                          display: 'inline-flex',
                          padding: '2px 8px',
                          borderRadius: '999px',
                          fontSize: '11px',
                          fontWeight: 600,
                          background: st.bg,
                          color: st.color,
                          border: `1px solid ${st.border}`,
                        }}
                      >
                        {st.label}
                      </span>
                    </td>
                    {showActions ? (
                      <td
                        style={{ padding: `${padV}px ${padH}px`, textAlign: 'right', whiteSpace: 'nowrap' }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <button
                          type="button"
                          style={iconButtonStyle(canConfirmRow(row))}
                          disabled={!canConfirmRow(row)}
                          title={canConfirmRow(row) ? 'Confirmar recepción' : 'Solo se confirma en Borrador'}
                          aria-label="Confirmar recepción"
                          onClick={() => canConfirmRow(row) && onConfirm?.(row)}
                        >
                          <PackageCheck size={14} />
                        </button>
                      </td>
                    ) : null}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default PurchaseReceiptsTable;
